import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, MapPin, Check, ChevronDown } from "lucide-react";
import {
  COUNTIES,
  ALL_COUNTIES_LABEL,
  normalizeCountyName,
  getCountyByName,
} from "./KenyaCounties";
import type { County } from "./KenyaCounties";

// In-app county picker: a modal with live search and a scrollable list of the
// 47 counties, each shown with its county logo.
//
// Renders through a portal straight into document.body, which is OUTSIDE the
// app's themed root — the CSS variables (--bg-container, --text-primary, ...)
// don't exist there. So, like the search overlay, it takes the `theme` prop
// and uses real colors instead of var(--...).
//
// The county list is static, so there is no data fetching here; it needs no
// extra dependency beyond what the app already uses.

// Round white "badge" the logo sits in. White keeps every logo legible in dark
// mode too, and the inset keeps wide logos from being clipped by the circle.
export function CountyLogo({
  county,
  size = 48,
}: {
  county: County;
  size?: number;
}) {
  // 0 = /counties/<slug>.webp, 1 = /<slug>.webp, 2 = give up (letter badge)
  const [attempt, setAttempt] = useState(0);
  const failed = attempt >= 2;
  return (
    <span
      className="shrink-0 rounded-full bg-white flex items-center justify-center overflow-hidden ring-1 ring-black/10"
      style={{ width: size, height: size }}
    >
      {failed ? (
        <span
          className="font-black text-neutral-500"
          style={{ fontSize: size * 0.4 }}
        >
          {county.name.charAt(0)}
        </span>
      ) : (
        <img
          src={attempt === 0 ? county.logo : county.logoFallback}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setAttempt((a) => a + 1)}
          className="object-contain"
          style={{ width: "76%", height: "76%" }}
        />
      )}
    </span>
  );
}

// The small "logo + county name + chevron" button that opens the dialog.
// Text is pure white in dark mode and pure black in light mode, in Arial.
// `compact` is the tighter version used on the Home page header row.
export function CountyFilterButton({
  selectedCounty,
  onClick,
  theme,
  compact = false,
  accentColor,
}: {
  selectedCounty: string;
  onClick: () => void;
  theme: "dark" | "light";
  compact?: boolean;
  accentColor?: string;
}) {
  const county = getCountyByName(selectedCounty);
  const color = theme === "dark" ? "#FFFFFF" : "#000000";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-label={`County: ${selectedCounty}. Tap to change`}
      className={`flex items-center rounded-full active:scale-95 transition-all hover:bg-[var(--surface-soft-hover)] ${
        compact
          ? "shrink-0 gap-1.5 py-1 pl-1 pr-2 border border-[var(--border-soft)]"
          : "-ml-1 gap-2 py-1 pl-1 pr-2"
      }`}
      style={{ color, fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      {county ? (
        <CountyLogo county={county} size={compact ? 20 : 24} />
      ) : (
        <MapPin
          size={compact ? 14 : 16}
          style={{ color: accentColor || color }}
          className={compact ? "ml-0.5" : "ml-0.5"}
        />
      )}
      <span
        className={`font-bold whitespace-nowrap ${
          compact ? "text-xs max-w-[96px] truncate" : "text-sm"
        }`}
      >
        {selectedCounty}
      </span>
      <ChevronDown size={compact ? 12 : 16} className="shrink-0" />
    </button>
  );
}

export interface CountySelectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  // Called with the county name, or ALL_COUNTIES_LABEL for "All Counties".
  onSelectCounty: (countyName: string) => void;
  // Currently selected county name (or ALL_COUNTIES_LABEL).
  selectedCounty: string;
  theme: "dark" | "light";
  // Accent for the "All Counties" icon / selected check (defaults to blue).
  accentColor?: string;
}

export default function CountySelectDialog({
  isOpen,
  onClose,
  onSelectCounty,
  selectedCounty,
  theme,
  accentColor = "#3B82F6",
}: CountySelectDialogProps) {
  const isDark = theme === "dark";
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const q = normalizeCountyName(query);
  const showAll = !q || "allcounties".includes(q);

  const results = useMemo(() => {
    if (!q) return COUNTIES;
    return COUNTIES.filter((c) => normalizeCountyName(c.name).includes(q));
  }, [q]);

  const choose = (name: string) => {
    onSelectCounty(name);
    onClose();
  };

  // Fresh search each time it opens, autofocus, Escape closes, Enter picks the
  // top match, and the page behind is locked from scrolling until it closes.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 80);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && q) {
        if (results[0]) choose(results[0].name);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, q, results]);

  if (typeof document === "undefined") return null;

  const rowBase =
    "w-full flex items-center gap-4 px-6 py-3 text-left transition-colors";
  const rowHover = isDark ? "hover:bg-white/[0.07]" : "hover:bg-black/[0.05]";
  const rowActive = isDark ? "bg-white/[0.07]" : "bg-black/[0.05]";

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="county-dialog-backdrop"
          className="fixed inset-0 z-[210] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            background: isDark ? "rgba(6,6,9,0.72)" : "rgba(20,20,24,0.45)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
          onClick={onClose}
        >
          <motion.div
            key="county-dialog-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Select your county"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className={`w-full max-w-md max-h-[85vh] flex flex-col rounded-3xl border shadow-2xl overflow-hidden ${
              isDark
                ? "bg-black border-white/10 text-white shadow-black/60"
                : "bg-white border-black/[0.07] text-neutral-900 shadow-black/20"
            }`}
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold">Select your county</h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className={`-mr-2 p-2 rounded-full transition-colors ${
                    isDark
                      ? "text-white/70 hover:text-white hover:bg-white/10"
                      : "text-neutral-500 hover:text-neutral-900 hover:bg-black/5"
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              <div
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${
                  isDark
                    ? "bg-white/[0.06] border-white/10"
                    : "bg-neutral-100 border-black/[0.06]"
                }`}
              >
                <Search
                  size={18}
                  className={isDark ? "text-white/40" : "text-neutral-400"}
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by county name"
                  autoComplete="off"
                  spellCheck={false}
                  className={`min-w-0 flex-1 bg-transparent text-base outline-none ${
                    isDark
                      ? "text-white placeholder:text-white/35"
                      : "text-neutral-900 placeholder:text-neutral-400"
                  }`}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      inputRef.current?.focus();
                    }}
                    aria-label="Clear search"
                    className={isDark ? "text-white/50" : "text-neutral-400"}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <style>{`
              .county-scroll { scrollbar-width: thin; scrollbar-color: var(--cs-thumb) var(--cs-track); }
              .county-scroll::-webkit-scrollbar { width: 10px; }
              .county-scroll::-webkit-scrollbar-track { background: var(--cs-track); }
              .county-scroll::-webkit-scrollbar-thumb { background: var(--cs-thumb); border-radius: 9999px; border: 2px solid var(--cs-track); }
              .county-scroll::-webkit-scrollbar-thumb:hover { background: var(--cs-thumb-hover); }
              .county-scroll::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
              .county-scroll::-webkit-scrollbar-corner { background: var(--cs-track); }
            `}</style>
            <div
              className="county-scroll flex-1 overflow-y-auto overscroll-contain pb-3"
              style={
                {
                  colorScheme: isDark ? "dark" : "light",
                  "--cs-track": isDark ? "#000000" : "#FFFFFF",
                  "--cs-thumb": isDark
                    ? "rgba(255,255,255,0.28)"
                    : "rgba(0,0,0,0.25)",
                  "--cs-thumb-hover": isDark
                    ? "rgba(255,255,255,0.45)"
                    : "rgba(0,0,0,0.4)",
                } as React.CSSProperties
              }
            >
              {showAll && (
                <button
                  type="button"
                  onClick={() => choose(ALL_COUNTIES_LABEL)}
                  className={`${rowBase} ${rowHover} ${
                    selectedCounty === ALL_COUNTIES_LABEL ? rowActive : ""
                  }`}
                >
                  <span
                    className="shrink-0 rounded-full flex items-center justify-center w-12 h-12"
                    style={{
                      background: `${accentColor}22`,
                      color: accentColor,
                    }}
                  >
                    <MapPin size={22} />
                  </span>
                  <span className="flex-1 text-lg font-semibold">
                    {ALL_COUNTIES_LABEL}
                  </span>
                  {selectedCounty === ALL_COUNTIES_LABEL && (
                    <Check size={20} style={{ color: accentColor }} />
                  )}
                </button>
              )}

              {results.map((county) => {
                const active = selectedCounty === county.name;
                return (
                  <button
                    key={county.id}
                    type="button"
                    onClick={() => choose(county.name)}
                    className={`${rowBase} ${rowHover} ${
                      active ? rowActive : ""
                    }`}
                  >
                    <CountyLogo county={county} size={48} />
                    <span className="flex-1 text-lg font-semibold">
                      {county.name}
                    </span>
                    {active && <Check size={20} style={{ color: accentColor }} />}
                  </button>
                );
              })}

              {results.length === 0 && (
                <div
                  className={`px-6 py-14 text-center text-sm ${
                    isDark ? "text-white/50" : "text-neutral-500"
                  }`}
                >
                  No county matches &ldquo;{query}&rdquo;
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
